import { memo } from 'react'
import { cn } from '@/lib/utils'
import { DICE_IMAGES, DICE_MAX_VALUES } from '@/types/dice'
import type { DiceType } from '@/types/dice'

interface DiceTest {
  attributeName: string
  attributeValue: number
  bonus: number
  total: number
  difficulty?: number
  outcome?: 'success' | 'failure'
  /** Vantagem / desvantagem: rolou dois dados e valeu um. */
  advantage?: 'vantagem' | 'desvantagem'
  /** O dado que NAO valeu — aparece apagado ao lado, nunca some. */
  droppedResult?: number
  /** Critico natural (fase 3): 'acerto' no 20, 'erro' no 1. Vem do servidor,
   *  que o derivou do dado que VALEU — nao se recalcula aqui. */
  critical?: 'acerto' | 'erro'
  /** O que estava sendo TENTADO, nomeado pelo mestre no pedido. E o que faz o
   *  historico da mesa registrar o acontecimento, e nao so o atributo. */
  actionName?: string
}

/** Bandeja: varios dados numa jogada so (fase 3 do modulo de dados). */
interface DicePool {
  spec: { type: string; result: number }[]
  kept: number[]
  rule?: string | null
  expression?: string | null
  /** Parcelas da ficha somadas (chips de atributo). */
  modifiers?: { id: string; name: string; value: number }[]
  /** Total com as parcelas somadas. */
  total?: number | null
}

interface DiceRollDisplayProps {
  /** "POOL" quando a jogada foi uma bandeja (ver `pool`). */
  diceType: DiceType | 'POOL'
  result: number
  isOwnMessage: boolean
  /** Teste de atributo: mostra a conta e o veredito abaixo do dado. */
  test?: DiceTest
  /** Bandeja: mostra cada dado, com os descartados apagados. */
  pool?: DicePool
}

/**
 * Um dado rolado: a imagem do sólido com o resultado numa FICHA sobreposta.
 *
 * A ficha existe desde 2026-07-27 (decisão 6 do docs/PLANO_AREA_DO_CHAT.md) e a
 * razão é concreta: os PNGs de `/assets/dice/` **não são dados em branco**. O
 * `d6.png` tem um "6" impresso na face e o `d20.png` mostra 20/18/15/13/11/7 de
 * uma vez. Sem a ficha, dois números disputam o mesmo dado e o aluno não sabe
 * qual é o dele. Com ela, os números impressos em volta leem como textura.
 *
 * Disco claro sólido, número escuro: é a ficha de jogo que se põe sobre o pano
 * da mesa. O Paulo escolheu este desenho entre cinco no mockup ("gostei de
 * ficha") — pedira algo que "pareça dado rolado sobre a mesa".
 *
 * **O dado descartado nunca some** — o princípio já escrito na `PoolDisplay`
 * logo abaixo. Quem diz "fora da conta" é o risco e o contorno tracejado, não
 * o apagamento: com a ficha clara, o desbotamento antigo (0.4) apagava o número
 * escuro justamente no dado que a regra existe para mostrar.
 */
function DadoComFicha({
  tipo,
  valor,
  tamanho = 44,
  dentro = true,
  mostrarTipo = false,
  animar = false,
}: {
  tipo: string
  valor: number
  tamanho?: number
  dentro?: boolean
  mostrarTipo?: boolean
  animar?: boolean
}) {
  const imagem = DICE_IMAGES[tipo as DiceType]

  return (
    <span className="inline-flex flex-col items-center gap-0.5 flex-shrink-0">
      <span
        className={cn('relative inline-flex items-center justify-center', !dentro && 'opacity-55')}
        style={{ width: tamanho, height: tamanho }}
      >
        {imagem && (
          <img
            src={imagem}
            alt=""
            aria-hidden="true"
            className={cn(
              'w-full h-full select-none object-contain',
              animar && 'motion-safe:animate-pulse'
            )}
            draggable={false}
          />
        )}
        <span
          className={cn(
            'absolute inline-flex items-center justify-center rounded-full',
            'bg-zinc-100 text-zinc-900 font-bold tabular-nums leading-none',
            'shadow-[0_1px_3px_rgba(0,0,0,0.45)]',
            dentro ? 'border border-zinc-400/60' : 'border border-dashed border-zinc-500 line-through'
          )}
          style={{
            width: Math.round(tamanho * 0.62),
            height: Math.round(tamanho * 0.62),
            fontSize: Math.max(11, Math.round(tamanho * 0.3)),
          }}
        >
          {valor}
        </span>
      </span>
      {mostrarTipo && (
        <span className="text-[10px] uppercase tracking-wide opacity-70 leading-none">
          {tipo.toLowerCase()}
        </span>
      )}
    </span>
  )
}

/**
 * Renderização visual do resultado de uma rolagem de dados.
 * Substitui o texto formatado (ex: "🎲 João rolou D20: **15**") por
 * SVG do dado + número grande em destaque.
 *
 * Cores especiais:
 * - resultado === 1: falha crítica (vermelho)
 * - resultado === máximo do dado: sucesso crítico (dourado)
 * - resto: cor neutra adaptada ao bubble (próprio vs alheio)
 *
 * Sendo um TESTE de atributo, o número grande passa a ser o total e a conta
 * aparece por extenso ("9 + 1 (Corpo) = 10 vs 12"). Mostrar a parcela de cada
 * coisa é deliberado: numa turma o aluno precisa poder conferir de onde veio o
 * número — ainda mais quando quem julga não for mais uma pessoa.
 */
export const DiceRollDisplay = memo(function DiceRollDisplay({
  diceType,
  result,
  isOwnMessage,
  test,
  pool,
}: DiceRollDisplayProps) {
  // Numa bandeja o "critico" perde sentido: 1 num d20 solto e drama, 1 numa
  // soma de cinco dados e so uma parcela.
  //
  // Duas coisas diferentes com o mesmo nome, e vale nao confundi-las: este par
  // e o BRILHO do numero, que existe desde antes e vale para qualquer dado
  // (um 6 num d6 acende). O `test.critical` da fase 3 e a REGRA — 20 ou 1 num
  // d20 de teste decidem o veredito sozinhos — e so o servidor a decide.
  const isCritFail = !pool && result === 1
  const isCritSuccess = !pool && result === DICE_MAX_VALUES[diceType as DiceType]

  if (pool) {
    return (
      <PoolDisplay
        pool={pool}
        result={result}
        isOwnMessage={isOwnMessage}
        test={test}
      />
    )
  }

  // O veredito e o critico: sao "resultado", entao ficam no card, ao lado do
  // numero. O resto e leitura, e desceu para o texto.
  const selo = test?.critical ? (
    <span
      data-testid="dado-critico"
      className={cn(
        'text-xs font-bold uppercase tracking-wide',
        test.critical === 'acerto' ? 'text-amber-300' : 'text-rose-400'
      )}
    >
      {test.critical === 'acerto'
        ? 'acerto crítico — 20 natural'
        : 'falha crítica — 1 natural'}
    </span>
  ) : test?.outcome ? (
    <span
      data-testid="dado-veredito"
      className={cn(
        'text-xs font-semibold uppercase tracking-wide',
        test.outcome === 'success' ? 'text-emerald-400' : 'text-rose-400'
      )}
    >
      {test.outcome === 'success' ? 'sucesso' : 'falha'}
    </span>
  ) : null

  return (
    // Duas faixas (redesenho de 2026-07-31, a pedido do Paulo): em cima o CARD,
    // com o que se ve de relance — os dados e o resultado. Embaixo, texto comum
    // que QUEBRA em varias linhas.
    //
    // Antes era tudo uma linha horizontal, e a conta levava `whitespace-nowrap`:
    // "D20 8 + 7 (Raciocinio Logico) = 15 vs 5" empurrava a largura do balao
    // para fora da tela no celular. Texto de teste nomeado so piorava.
    <div className="flex flex-col gap-1.5 py-1 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Dado unico com a MESMA ficha da bandeja: um tratamento so, para o
            aluno nao ter de reaprender a ler o dado quando a jogada tem um ou
            cinco. */}
        <DadoComFicha
          tipo={diceType}
          valor={result}
          tamanho={56}
          animar={isCritFail || isCritSuccess}
        />
        {/* O dado descartado pela vantagem fica ao lado, apagado e riscado — o
            mesmo tratamento do descarte da bandeja. Some-lo transformaria a
            mecanica num truque: "por que 17?" nao teria resposta na tela. */}
        {test?.advantage && typeof test.droppedResult === 'number' && (
          <span data-testid="dado-descartado" title={`${diceType} — fora da conta`}>
            <DadoComFicha
              tipo={diceType}
              valor={test.droppedResult}
              tamanho={40}
              dentro={false}
            />
          </span>
        )}
        <span
          data-testid="dado-numero"
          className={cn(
            'text-3xl font-bold tabular-nums leading-none',
            isCritFail && 'text-rose-400 drop-shadow-[0_0_6px_rgba(244,63,94,0.4)]',
            isCritSuccess && 'text-amber-300 drop-shadow-[0_0_6px_rgba(252,211,77,0.5)]',
            !isCritFail && !isCritSuccess &&
              (isOwnMessage ? 'text-white' : 'text-game-primary dark:text-zinc-100')
          )}
        >
          {test ? test.total : result}
        </span>
        {selo}
      </div>

      {/* A leitura: quebra em quantas linhas precisar, e nunca estica o balao. */}
      <div className="flex flex-col leading-snug min-w-0">
        <span
          className={cn(
            'text-xs uppercase tracking-wide opacity-80 break-words',
            isOwnMessage ? 'text-violet-100' : 'text-game-muted'
          )}
        >
          {test?.actionName
            ? `tentou ${test.actionName}`
            : test ? `testou ${test.attributeName}` : `rolou ${diceType}`}
          {test?.advantage && ` — com ${test.advantage}`}
        </span>
        {/* Com acao nomeada, o atributo desce uma linha em vez de sumir: quem
            confere a conta precisa saber de onde veio a parcela. */}
        {test?.actionName && (
          <span
            data-testid="dado-atributo-secundario"
            className={cn(
              'text-[11px] tracking-wide opacity-70 break-words',
              isOwnMessage ? 'text-violet-100' : 'text-game-muted'
            )}
          >
            {`teste de ${test.attributeName}`}
          </span>
        )}
        {test && (
          <span
            data-testid="dado-conta"
            className={cn(
              'text-xs tabular-nums mt-0.5 break-words',
              isOwnMessage ? 'text-violet-100/90' : 'text-game-secondary'
            )}
          >
            {`${diceType} ${result} + ${test.attributeValue} (${test.attributeName})`}
            {test.bonus !== 0 && ` ${test.bonus > 0 ? '+' : '−'} ${Math.abs(test.bonus)}`}
            {` = ${test.total}`}
            {test.difficulty != null && ` vs ${test.difficulty}`}
          </span>
        )}
      </div>
    </div>
  )
})

/**
 * Bandeja no chat: cada dado aparece, e o que a regra descartou aparece
 * APAGADO em vez de sumir.
 *
 * Isso e deliberado e e o mesmo principio da conta por extenso do teste de
 * atributo: numa turma, "manteve o maior" so ensina alguma coisa se der para
 * ver o que foi descartado. Esconder o dado que saiu de fora transformaria a
 * regra num truque de magica.
 */
function PoolDisplay({
  pool,
  result,
  isOwnMessage,
  test,
}: {
  pool: DicePool
  result: number
  isOwnMessage: boolean
  test?: DiceTest
}) {
  const mantidos = new Set(pool.kept)
  const contouSucessos = pool.rule === 'sucessos'
  // As parcelas da ficha entram na conta. Sem isto o balao mostrava os dados e
  // a soma crua, ignorando o atributo escolhido (bug relatado em uso).
  const parcelas = pool.modifiers ?? []
  const somaDosDados = pool.spec
    .filter((_, i) => mantidos.has(i))
    .reduce((soma, d) => soma + d.result, 0)
  const numeroGrande = test ? test.total : (pool.total ?? result)

  return (
    <div className="flex flex-col gap-2 py-1 min-w-0">
      {pool.expression && (
        <span
          data-testid="bandeja-notacao"
          className={cn(
            'text-xs font-mono tracking-tight opacity-80 break-words',
            isOwnMessage ? 'text-violet-100' : 'text-game-muted'
          )}
        >
          {pool.expression}
        </span>
      )}

      {/* Cada dado e o solido de verdade, com a ficha por cima — o pedido do
          Paulo era "que pareca dado rolado sobre a mesa, com resultados
          individuais mais o resultado da operacao". Eram quadradinhos com
          numero. O tipo (d6, d20) fica legivel embaixo de cada um: numa
          bandeja misturada, saber QUE dado deu aquele numero e parte da conta. */}
      <div className="flex flex-wrap items-end gap-2" data-testid="bandeja-dados">
        {pool.spec.map((dado, i) => {
          const dentro = mantidos.has(i)
          return (
            <span
              key={i}
              data-testid={dentro ? 'dado-mantido' : 'dado-descartado'}
              title={dentro ? dado.type : `${dado.type} — fora da conta`}
              className="inline-flex"
            >
              <DadoComFicha
                tipo={dado.type}
                valor={dado.result}
                dentro={dentro}
                mostrarTipo
              />
            </span>
          )
        })}
      </div>

      <div className="flex items-baseline gap-2">
        <span
          data-testid="dado-numero"
          className={cn(
            'text-3xl font-bold tabular-nums leading-none',
            isOwnMessage ? 'text-white' : 'text-game-primary dark:text-zinc-100'
          )}
        >
          {contouSucessos ? result : numeroGrande}
        </span>
        <span
          className={cn(
            'text-xs uppercase tracking-wide opacity-80',
            isOwnMessage ? 'text-violet-100' : 'text-game-muted'
          )}
        >
          {contouSucessos
            ? result === 1 ? 'sucesso' : 'sucessos'
            : pool.rule === 'maiores' ? `manteve ${pool.kept.length}` : 'total'}
        </span>
      </div>

      {!test && parcelas.length > 0 && (
        <span
          data-testid="dado-conta"
          className={cn(
            'text-xs tabular-nums',
            isOwnMessage ? 'text-violet-100/90' : 'text-game-secondary'
          )}
        >
          {somaDosDados}
          {parcelas.map(p => (
            <span key={p.id}>
              {` ${p.value >= 0 ? '+' : '−'} ${Math.abs(p.value)} (${p.name})`}
            </span>
          ))}
          {` = ${pool.total ?? somaDosDados}`}
        </span>
      )}

      {test && (
        <span
          data-testid="dado-conta"
          className={cn(
            'text-xs tabular-nums',
            isOwnMessage ? 'text-violet-100/90' : 'text-game-secondary'
          )}
        >
          {`${result} + ${test.attributeValue} (${test.attributeName})`}
          {test.bonus !== 0 && ` ${test.bonus > 0 ? '+' : '−'} ${Math.abs(test.bonus)}`}
          {` = ${test.total}`}
          {test.difficulty != null && ` vs ${test.difficulty}`}
        </span>
      )}

      {test?.outcome && (
        <span
          data-testid="dado-veredito"
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            test.outcome === 'success' ? 'text-emerald-400' : 'text-rose-400'
          )}
        >
          {test.outcome === 'success' ? 'sucesso' : 'falha'}
        </span>
      )}
    </div>
  )
}
